import { supabase } from '../lib/supabase';

export const sessionService = {
  /**
   * Create a new session
   */
  async createSession(sessionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case
      const dbData = {
        session_id: sessionData?.sessionId,
        appointment_id: sessionData?.appointmentId || null,
        user_id: user?.id,
        helper_id: sessionData?.helperId,
        session_type: sessionData?.sessionType,
        status: 'active',
        start_time: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('sessions')?.insert(dbData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create session error:', error);
      return { data: null, error };
    }
  },

  /**
   * End a session
   */
  async endSession(sessionId, duration) {
    try {
      const { data, error } = await supabase?.from('sessions')?.update({
          status: 'completed',
          end_time: new Date()?.toISOString(),
          duration
        })?.eq('id', sessionId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('End session error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get user's session history
   */
  async getSessionHistory() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('sessions')?.select(`
          *,
          helpers (
            name,
            type_label,
            profile_image,
            profile_image_alt
          ),
          session_ratings (
            rating,
            feedback,
            personal_notes,
            follow_up_scheduled,
            next_session_date
          )
        `)?.eq('user_id', user?.id)?.eq('status', 'completed')?.order('start_time', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      const sessions = data?.map(session => ({
        id: session?.id,
        sessionId: session?.session_id,
        helperName: session?.helpers?.name,
        helperRole: session?.helpers?.type_label,
        helperImage: session?.helpers?.profile_image,
        helperImageAlt: session?.helpers?.profile_image_alt,
        sessionType: session?.session_type,
        date: session?.start_time?.split('T')?.[0],
        time: session?.start_time?.split('T')?.[1]?.substring(0, 5),
        duration: session?.duration,
        topic: session?.topic || 'General Session',
        rating: session?.session_ratings?.[0]?.rating,
        feedback: session?.session_ratings?.[0]?.feedback,
        personalNotes: session?.session_ratings?.[0]?.personal_notes,
        followUpScheduled: session?.session_ratings?.[0]?.follow_up_scheduled,
        nextSessionDate: session?.session_ratings?.[0]?.next_session_date
      })) || [];

      return { data: sessions, error: null };
    } catch (error) {
      console.error('Get session history error:', error);
      return { data: [], error };
    }
  },

  /**
   * Add session message
   */
  async addSessionMessage(sessionId, messageText, senderRole) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('session_messages')?.insert({
          session_id: sessionId,
          sender_id: user?.id,
          sender_role: senderRole,
          message_text: messageText
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Add session message error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get session messages
   */
  async getSessionMessages(sessionId) {
    try {
      const { data, error } = await supabase?.from('session_messages')?.select('*')?.eq('session_id', sessionId)?.order('timestamp', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase
      const messages = data?.map(msg => ({
        id: msg?.id,
        sender: msg?.sender_role,
        text: msg?.message_text,
        timestamp: msg?.timestamp
      })) || [];

      return { data: messages, error: null };
    } catch (error) {
      console.error('Get session messages error:', error);
      return { data: [], error };
    }
  },

  /**
   * Add session rating
   */
  async addSessionRating(sessionId, helperId, ratingData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case
      const dbData = {
        session_id: sessionId,
        user_id: user?.id,
        helper_id: helperId,
        rating: ratingData?.rating,
        feedback: ratingData?.feedback,
        personal_notes: ratingData?.personalNotes,
        follow_up_scheduled: ratingData?.followUpScheduled,
        next_session_date: ratingData?.nextSessionDate
      };

      const { data, error } = await supabase?.from('session_ratings')?.insert(dbData)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Add session rating error:', error);
      return { data: null, error };
    }
  }
};